// Links from support reply emails: {web}/Menu?support=SUP-XXXXXXXXXXXX opens that conversation in
// the composer's panel. The id is read as the page loads, before the router reads the address, so
// the sign-in redirect (/?redirect=...) never carries it; it waits in this tab (conversationStore)
// until the signed-in customer's composer shows, and comes out of the address bar at once. An email
// sign-in link opens in another tab, so the page it goes to after signing in carries the id instead.
import {
  readPendingConversation,
  writePendingConversation,
} from './conversationStore';

const PARAM = 'support';
const CONVERSATION_ID = /^SUP-[A-Z0-9]{6,24}$/;

function decode(value) {
  try {
    return decodeURIComponent(value.replace(/\+/g, ' '));
  } catch (e) {
    // Malformed escapes: not a parameter we use.
    return null;
  }
}

function pairs(search) {
  return (search || '').replace(/^\?/, '').split('&');
}

function isSupportPair(pair) {
  return decode(pair.split('=')[0]) === PARAM;
}

/** The conversation id in a query string (?support=SUP-...), or null for none or anything else. */
export function supportLinkFrom(search) {
  const pair = pairs(search).filter(isSupportPair)[0];
  if (!pair || pair.indexOf('=') < 0) return null;
  const value = decode(pair.slice(pair.indexOf('=') + 1));
  return value && CONVERSATION_ID.test(value) ? value : null;
}

/** The query string without its support parameter; the rest stays exactly as it was. */
export function withoutSupportLink(search) {
  const rest = pairs(search)
    .filter(pair => !isSupportPair(pair))
    .join('&');
  return rest ? `?${rest}` : '';
}

/** A path (with query and hash) without its support parameter. */
export function withoutSupportLinkInPath(path) {
  if (!path) return path;
  const [beforeHash, ...hashParts] = path.split('#');
  const hash = hashParts.length ? `#${hashParts.join('#')}` : '';
  const queryAt = beforeHash.indexOf('?');
  if (queryAt < 0) return path;
  return `${beforeHash.slice(0, queryAt)}${withoutSupportLink(
    beforeHash.slice(queryAt)
  )}${hash}`;
}

/**
 * Where to go after signing in, with the link waiting in this tab added (?support=), for a sign-in
 * that ends in another tab (magicLink.js): that tab's storage doesn't have it. No path means /Menu,
 * as there; a path with its own link, or no link waiting, stays as it is.
 */
export function withPendingSupportLink(path) {
  const conversationId = readPendingConversation();
  if (!conversationId || !CONVERSATION_ID.test(conversationId)) return path;
  const [beforeHash, ...hashParts] = (path || '/Menu').split('#');
  const hash = hashParts.length ? `#${hashParts.join('#')}` : '';
  const queryAt = beforeHash.indexOf('?');
  const pathname = queryAt < 0 ? beforeHash : beforeHash.slice(0, queryAt);
  const search = queryAt < 0 ? '' : beforeHash.slice(queryAt);
  if (supportLinkFrom(search)) return path;
  const rest = withoutSupportLink(search);
  const query = rest ? `${rest}&` : '?';
  return `${pathname}${query}${PARAM}=${conversationId}${hash}`;
}

/**
 * Call as the page loads, before the router starts (index.js; SupportDock reads a later one through
 * the router). A valid ?support= id is kept for the composer and taken out of the address bar, so a
 * reload or a copied address doesn't open it again; anything else is left alone. Returns the id.
 */
export function captureSupportLink(
  location = window.location,
  history = window.history
) {
  const conversationId = supportLinkFrom(location.search);
  if (!conversationId) return null;
  writePendingConversation(conversationId);
  try {
    history.replaceState(
      history.state,
      document.title,
      `${location.pathname}${withoutSupportLink(location.search)}${
        location.hash
      }`
    );
  } catch (e) {
    // The id is kept all the same; only the address keeps the parameter.
  }
  return conversationId;
}



// WEBPACK FOOTER //
// ./src/support/deepLink.js